import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { Sentry } from "@/lib/sentry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBEquipmentServiceTicket } from "@/lib/db-types";
import type { ServiceTicketStatus } from "@/lib/equipment/constants";

const BUCKET = "equipment-media";

/**
 * Servicesaker (VIB-2921) fra viewet equipment_service_ticket_list. Alle
 * interne ser sakene; kostnaden er NULL for andre enn admin.
 */
export function useServiceTickets(enabled = true) {
  return useQuery({
    queryKey: ["equipment-service"],
    enabled,
    queryFn: async (): Promise<DBEquipmentServiceTicket[]> => {
      const { data, error } = await supabase
        .from("equipment_service_ticket_list")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as DBEquipmentServiceTicket[];
    },
  });
}

/** Signert lenke til bildet av skaden (privat bøtte). */
export function useServiceImageUrl(path: string | null) {
  return useQuery({
    queryKey: ["equipment-service", "image", path],
    enabled: !!path,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path!, 3600);
      if (error) throw error;
      return data.signedUrl;
    },
    staleTime: 30 * 60 * 1000,
  });
}

export interface DamageReport {
  itemId: string;
  description: string;
  usable: boolean;
  image: File | null;
}

/**
 * Meld skade. reported_by er den ekte innloggede brukeren (RLS krever det).
 * «Ikke brukbar» setter utstyret «På service» i en trigger. Etterpå varsler
 * equipment-reminders utstyrsansvarlige i varslingsbjella; feiler varselet, er saken
 * likevel lagret, så feilen går til Sentry og ikke til brukeren.
 */
export function useReportDamage() {
  const qc = useQueryClient();
  const { selfUser } = useImpersonation();
  return useMutation({
    mutationFn: async ({ itemId, description, usable, image }: DamageReport) => {
      if (!selfUser) throw new Error("Fant ikke innlogget bruker");
      const id = crypto.randomUUID();
      let imagePath: string | null = null;
      if (image) {
        const ext = image.name.split(".").pop()?.toLowerCase() || "jpg";
        imagePath = `service/${id}/${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage.from(BUCKET).upload(imagePath, image, { contentType: image.type });
        if (error) throw new Error(`Opplastingen av bildet feilet: ${error.message}`);
      }
      const { error } = await supabase.from("equipment_service_tickets").insert({
        id,
        item_id: itemId,
        description: description.trim(),
        usable,
        image_path: imagePath,
        reported_by: selfUser.id,
      });
      if (error) throw new Error(`Kunne ikke melde skaden: ${error.message}`);

      const { error: notifyError } = await supabase.functions.invoke("equipment-reminders", { body: { ticket_id: id } });
      if (notifyError) Sentry.captureException(notifyError, { tags: { feature: "equipment-service" } });
      return id;
    },
    onSuccess: async (_id, { itemId }) => {
      await invalidateFor(qc, "equipment_service_tickets", { equipmentId: itemId });
      await invalidateFor(qc, "equipment_items", { equipmentId: itemId });
    },
  });
}

export interface ServiceTicketPatch {
  status: ServiceTicketStatus;
  vendor: string | null;
  /** Bare admin; utelates for andre. */
  cost?: number | null;
}

/** Endre en sak (admin og utstyrsansvarlige). «Ferdig» frigir utstyret i triggeren. */
export function useUpdateServiceTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; itemId: string; patch: ServiceTicketPatch }) => {
      const { data, error } = await supabase.from("equipment_service_tickets").update(patch).eq("id", id).select("id");
      if (error) throw new Error(`Kunne ikke lagre: ${error.message}`);
      if ((data ?? []).length !== 1) throw new Error("Du har ikke tilgang til å endre servicesaken");
    },
    onSuccess: async (_v, { itemId }) => {
      await invalidateFor(qc, "equipment_service_tickets", { equipmentId: itemId });
      await invalidateFor(qc, "equipment_items", { equipmentId: itemId });
    },
  });
}
